/**
 * MultimediaService - Refactorizado para usar Clean Architecture
 * 
 * Este servicio mantiene compatibilidad con la API anterior mientras
 * internamente usa el repositorio y modelos de dominio.
 * 
 * MIGRACIÓN: Los DTOs e interfaces se han movido a:
 * - DTOs: core/infrastructure/dto/multimedia.dto.ts
 * - Modelos: core/domain/models/multimedia.model.ts
 * 
 * NOTA: Este servicio mantiene lógica de cache porque actúa como servicio
 * de aplicación, no solo como repositorio.
 */

import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { MultimediaRepository } from '@domain/repositories/multimedia.repository';
import { MULTIMEDIA_REPOSITORY_TOKEN } from '@domain/repositories/multimedia.repository.token';
import { MultimediaMapper } from '@infra/mappers/multimedia.mapper';
import { MultimediaItem as DomainMultimediaItem } from '@domain/models/multimedia.model';
import { MultimediaItemDto } from '@infra/dto/multimedia.dto';

// Re-exportar tipo para compatibilidad (usar desde domain/models)
export type MultimediaItem = {
  multimediaId: string;
  url: string;
  description?: string;
  contentTypeId: string;
  showInPublicHome: boolean;
  showInPrivateHome: boolean;
  createdAt: string;
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class MultimediaService {
  private repository = inject<MultimediaRepository>(MULTIMEDIA_REPOSITORY_TOKEN);
  
  // Cache con TTL (Time To Live) de 30 minutos (1,800,000 ms)
  private readonly CACHE_TTL = 30 * 60 * 1000; // 30 minutos
  
  // Cache para multimedia pública (en formato legacy para compatibilidad)
  private publicMultimediaCache: CacheEntry<MultimediaItem[]> | null = null;
  
  // Cache para multimedia privada (en formato legacy para compatibilidad)
  private privateMultimediaCache: CacheEntry<MultimediaItem[]> | null = null;

  /**
   * Verifica si el cache es válido (no ha expirado)
   */
  private isCacheValid(cache: CacheEntry<any> | null): boolean {
    if (!cache) return false;
    const now = Date.now();
    return (now - cache.timestamp) < this.CACHE_TTL;
  }

  /**
   * Obtener multimedia pública para el home
   * Implementa cache en el frontend para reducir llamadas HTTP innecesarias
   * Internamente usa el repositorio con modelos de dominio
   */
  getPublicMultimedia(): Observable<MultimediaItem[]> {
    // Si hay cache válido, retornar datos del cache
    if (this.isCacheValid(this.publicMultimediaCache)) {
      return of(this.publicMultimediaCache!.data);
    }

    // Si no hay cache o expiró, hacer llamada HTTP y actualizar cache
    return this.repository.getPublicMultimedia().pipe(
      map(domainItems => domainItems.map(item => this.domainToLegacy(item))),
      tap((data) => {
        this.publicMultimediaCache = {
          data: data,
          timestamp: Date.now()
        };
      })
    );
  }

  /**
   * Obtener multimedia privada para el home
   * Implementa cache en el frontend para reducir llamadas HTTP innecesarias
   * Internamente usa el repositorio con modelos de dominio
   */
  getPrivateMultimedia(): Observable<MultimediaItem[]> {
    // Si hay cache válido, retornar datos del cache
    if (this.isCacheValid(this.privateMultimediaCache)) {
      return of(this.privateMultimediaCache!.data);
    }

    // Si no hay cache o expiró, hacer llamada HTTP y actualizar cache
    return this.repository.getPrivateMultimedia().pipe(
      map(domainItems => domainItems.map(item => this.domainToLegacy(item))),
      tap((data) => {
        this.privateMultimediaCache = {
          data: data,
          timestamp: Date.now()
        };
      })
    );
  }

  /**
   * Invalidar el cache de multimedia pública
   * Útil cuando se sabe que los datos han cambiado
   */
  invalidatePublicCache(): void {
    this.publicMultimediaCache = null;
  }

  /**
   * Invalidar el cache de multimedia privada
   * Útil cuando se sabe que los datos han cambiado
   */
  invalidatePrivateCache(): void {
    this.privateMultimediaCache = null;
  }

  /**
   * Invalidar todo el cache
   */
  invalidateAllCache(): void {
    this.publicMultimediaCache = null;
    this.privateMultimediaCache = null;
  }

  /**
   * Convierte un modelo de dominio MultimediaItem a formato legacy
   */
  private domainToLegacy(domain: DomainMultimediaItem): MultimediaItem {
    return {
      multimediaId: domain.id,
      url: domain.url,
      description: domain.description,
      contentTypeId: domain.contentTypeId,
      showInPublicHome: domain.showInPublicHome,
      showInPrivateHome: domain.showInPrivateHome,
      createdAt: domain.createdAt.toISOString()
    };
  }
}

